import { useCallback, useEffect, useRef, useState } from 'react';
import { renderPdfToBlob } from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { saveBlobAs } from '@/lib/utils';
import {
  useUploadAllocationDeclarationFileMutation,
  useSaveAllocationDeclarationMutation,
  useLazyGetAllocationDeclarationFileQuery,
  useLazyGetAllocationDeclarationQuery,
  useCreateAllocationShipmentMutation,
} from './api/allocationsApi';
import { buildDeclarationPdf } from './components/DeclarationPdfDocument';
import { declarationFileName } from './constants/declarationDocumentFields';
import { buildEditDeclarationValues } from './constants/editDeclarationValues';

/**
 * The declaration PDF for one allocation: the stored file when the server already holds one,
 * otherwise rendered from `values`. Nothing is written — the upload is Confirm's first step.
 * `previewBlob` is whichever of the two, for <PDFViewer>; `hasStoredFile` says the read found
 * one, which is the only thing Download can hand over.
 */
export const useDeclarationPdf = ({
  module,
  allocationId,
  values,
  enabled = true,
  // The file the server already holds, if any — the prefill query carries its id.
  storedFileId,
  // An edit must upload the document it just rendered, so only the previews reuse what is stored.
  reuseStoredFile = true,
}) => {
  const t = useTranslations();
  const [fetchFile] = useLazyGetAllocationDeclarationFileQuery();
  const [pdf, setPdf] = useState({ previewBlob: null, hasStoredFile: false });
  const valuesRef = useRef(values);
  valuesRef.current = values;
  // The session this hook has already produced a document for.
  const renderedRef = useRef(null);

  // Not keyed on `values`: the save invalidates the read behind them, and that refetch must not re-render mid-confirm.
  const session = enabled && allocationId ? `${module}:${allocationId}` : null;

  useEffect(() => {
    if (!session || renderedRef.current === session) return undefined;
    let live = true;
    setPdf({ previewBlob: null, hasStoredFile: false });

    const resolvePdf = async () => {
      // A saved declaration already has its file; a fresh render would not be what shipped.
      if (reuseStoredFile && storedFileId) {
        const stored = await fetchFile({ module, id: allocationId });
        if (!live) return;
        if (stored.data?.size > 0) {
          renderedRef.current = session;
          setPdf({ previewBlob: stored.data, hasStoredFile: true });
          return;
        }
      }

      const blob = await renderPdfToBlob(buildDeclarationPdf(t, valuesRef.current));
      if (!live) return;
      // Claimed here, not above: an effect torn down before this never rendered anything.
      renderedRef.current = session;
      setPdf({ previewBlob: blob, hasStoredFile: false });
    };

    resolvePdf();

    return () => {
      live = false;
    };
  }, [session, t, module, allocationId, reuseStoredFile, storedFileId, fetchFile]);

  // Closing the preview ends the session, so reopening reads and renders afresh.
  useEffect(() => {
    if (!enabled) renderedRef.current = null;
  }, [enabled]);

  return pdf;
};

/** `(allocationId, blob) => Promise<fileId|undefined>` — Confirm's first step, before the save. */
export const useUploadDeclarationFile = (module) => {
  const [uploadFile] = useUploadAllocationDeclarationFileMutation();

  return useCallback(
    async (allocationId, file) => {
      if (!allocationId || !file) return undefined;
      const upload = await uploadFile({ module, id: allocationId, file });
      return upload.data ?? undefined;
    },
    [uploadFile, module],
  );
};

/** Hands the viewer the document the screen just showed, never a second round trip. */
export const saveRenderedDeclaration = (blob, values) => {
  if (blob) saveBlobAs(blob, declarationFileName(values));
};

/** `(values, fileId) => Promise` — POST generates, PUT edits; the file is already uploaded. */
export const useSaveDeclaration = ({ module, allocationId, isEdit = false }) => {
  const [saveDeclaration, { isLoading: isSaving }] = useSaveAllocationDeclarationMutation();

  const save = useCallback(
    (values, declarationFileId) =>
      saveDeclaration({ module, id: allocationId, values, declarationFileId, isEdit }),
    [saveDeclaration, module, allocationId, isEdit],
  );

  return { save, isSaving };
};

/**
 * `(allocationId, values, previewBlob?) => Promise<void>` — the stored PDF when the server holds
 * one, else the preview on screen, so Download always hands something over. The id is an
 * argument, not a binding: a row action downloads its own row.
 */
export const useDownloadDeclaration = (module) => {
  const [fetchFile] = useLazyGetAllocationDeclarationFileQuery();

  return useCallback(
    async (allocationId, values, previewBlob) => {
      if (!allocationId) return;
      const { data } = await fetchFile({ module, id: allocationId });
      if (data) saveBlobAs(data, declarationFileName(values));
      else saveRenderedDeclaration(previewBlob, values);
    },
    [fetchFile, module],
  );
};

/**
 * `(row) => Promise<void>` — a list row's Download: the stored PDF when there is one, else
 * rendered in the browser from the declaration (bulk-generated and older rows have no file).
 * The render is only handed over, never uploaded.
 */
export const useDownloadRowDeclaration = (module) => {
  const t = useTranslations();
  const [fetchFile] = useLazyGetAllocationDeclarationFileQuery();
  const [fetchDeclaration] = useLazyGetAllocationDeclarationQuery();

  return useCallback(
    async (row) => {
      if (!row?.id) return;
      if (row.declarationFileId != null) {
        const { data } = await fetchFile({ module, id: row.id });
        if (data?.size > 0) {
          saveBlobAs(data, declarationFileName({ idNumber: row.declarationNo }));
          return;
        }
      }
      const { data: declaration } = await fetchDeclaration({ id: row.id });
      if (!declaration) return;
      const values = buildEditDeclarationValues(row, declaration);
      const blob = await renderPdfToBlob(buildDeclarationPdf(t, values));
      saveRenderedDeclaration(blob, values);
    },
    [t, fetchFile, fetchDeclaration, module],
  );
};

/** `(allocation, shipments) => Promise<boolean>` — one POST per card, in order; stops at the first rejection. */
export const useSaveShipmentDetails = (module) => {
  const [createShipment, { isLoading: isSaving }] = useCreateAllocationShipmentMutation();

  const save = useCallback(
    async (allocation, shipments) => {
      if (!allocation?.orderId) return false;
      for (const shipment of shipments) {
        const result = await createShipment({
          module,
          id: allocation.id,
          orderId: allocation.orderId,
          shipment,
          unitByType: allocation.unitByType,
        });
        if (result.error) return false;
      }
      return true;
    },
    [createShipment, module],
  );

  return { save, isSaving };
};
